import { useCallback, useEffect, useState } from 'react';

import {
  persistTheme,
  readStoredTheme,
  resolveEffectiveTheme,
  THEME_ATTRIBUTE,
  type ThemeMode,
} from './theme';

export interface UseThemeResult {
  readonly theme: ThemeMode;
  setTheme(mode: ThemeMode): void;
  toggleTheme(): void;
}

/**
 * React binding over the framework-agnostic theme core.
 *
 * State is read from the DOM on mount (`document.documentElement`) rather
 * than reconstructed from scratch, because the no-flash bootstrap script
 * (theme-script.ts) already set the attribute before React mounted — this
 * hook's job is to stay in sync with that decision, not to make it again and
 * risk disagreeing with what the user is already looking at.
 */
export function useTheme(): UseThemeResult {
  const [theme, setThemeState] = useState<ThemeMode>(() => readInitialTheme());

  useEffect(() => {
    // Reacts to the OS theme changing while the page is open, but only when
    // the user has not made an explicit choice — an explicit choice must not
    // be silently overridden by the system.
    const media = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (): void => {
      if (readStoredTheme() !== null) return;
      applyTheme(resolveEffectiveTheme(null, media.matches));
    };

    media.addEventListener('change', handleChange);
    return () => {
      media.removeEventListener('change', handleChange);
    };
  }, []);

  const applyTheme = useCallback((mode: ThemeMode) => {
    document.documentElement.setAttribute(THEME_ATTRIBUTE, mode);
    setThemeState(mode);
  }, []);

  const setTheme = useCallback(
    (mode: ThemeMode) => {
      applyTheme(mode);
      persistTheme(mode);
    },
    [applyTheme],
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [setTheme, theme]);

  return { theme, setTheme, toggleTheme };
}

function readInitialTheme(): ThemeMode {
  if (typeof document === 'undefined') return 'light';

  const explicit = document.documentElement.getAttribute(THEME_ATTRIBUTE);
  const prefersDark =
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

  return resolveEffectiveTheme(explicit, prefersDark);
}
